import { createSignal, onMount, For, Show } from 'solid-js';
import { useSFetch } from '../hooks/sfetch';
import { useToast } from '../components/MessageBox';
import Text from '../components/Text';
import { showConfirm } from '../components/Dialog';
import { useTranslation } from '../hooks/useTranslation';
import { OtherAccount } from '../models/OtherAccount';
import { useNavigate } from '@solidjs/router';
import { firstError } from '../api/envelope';

export default function AccountSwitcher() {
    const toast = useToast();
    const sfetch = useSFetch();
    const navigate = useNavigate();
    const { t } = useTranslation();

    const [otherAccounts, setOtherAccounts] = createSignal<OtherAccount[]>([]);
    const [currentUser, setCurrentUser] = createSignal<any>(null);
    const [isLoading, setIsLoading] = createSignal(false);

    onMount(() => {
        loadCurrentUser();
        loadOtherAccounts();
    });

    async function loadCurrentUser() {
        try {
            const response = await fetch("/api/User", {
                method: "GET",
                credentials: "include" // Include cookies for authentication
            });
            
            if (response.ok) {
                const data = await response.json();
                setCurrentUser(data.value || data);
            } else if (response.status === 401 || response.status === 403) {
                setCurrentUser(null);
                console.warn("User not authenticated, skipping current user load");
            } else {
                const body = await response.json().catch(() => undefined);
                toast?.error(firstError(body) ?? t("ACCOUNT_SWITCHER_CURRENT_LOAD_FAILED"));
            }
        } catch (error: any) {
            setCurrentUser(null);
            toast?.error(error?.message ?? t("ACCOUNT_SWITCHER_CURRENT_LOAD_FAILED"));
        }
    }

    async function loadOtherAccounts() {
        try {
            setIsLoading(true);
            const response = await sfetch("/api/User/OtherAccounts");
            
            if (response.ok) {
                setOtherAccounts(response.value || []);
            } else {
                toast?.error(firstError(response) ?? t("ACCOUNT_SWITCHER_LIST_LOAD_FAILED"));
            }
        } catch (error: any) {
            toast?.error(error?.message ?? t("ACCOUNT_SWITCHER_LIST_LOAD_FAILED"));
        } finally {
            setIsLoading(false);
        }
    }

    async function switchToAccount(account: OtherAccount) {
        try {
            setIsLoading(true);
            const response = await sfetch("/api/User/SwitchAccount", {
                method: "POST",
                headers: {
                    "Content-Type": "application/json"
                },
                body: JSON.stringify(account)
            });

            if (response.ok) {
                toast?.info(t("ACCOUNT_SWITCHER_SWITCHED", account.username));

                const urlParams = new URLSearchParams(window.location.search);
                const redirectUri = urlParams.get("redirectUri");

                if (redirectUri) {
                    window.location.href = decodeURIComponent(redirectUri);
                } else {
                    navigate('/me');
                }
            } else {
                toast?.error(firstError(response) ?? t("ACCOUNT_SWITCHER_SWITCH_FAILED", account.username));
            }
        } catch (error: any) {
            toast?.error(error?.message ?? t("ACCOUNT_SWITCHER_SWITCH_FAILED", account.username));
        } finally {
            setIsLoading(false);
        }
    }

    async function deleteAccount(accountName: string) {
        if (!await showConfirm(t("ACCOUNT_REMOVE_CONFIRM", accountName), true)) {
            return;
        }

        try {
            setIsLoading(true);
            const response = await sfetch(`/api/User/OtherAccounts/${encodeURIComponent(accountName)}`, {
                method: "DELETE"
            });

            if (response.ok) {
                setOtherAccounts(otherAccounts().filter(acc => acc.username !== accountName));
                toast?.info(t("ACCOUNT_SWITCHER_REMOVED", accountName));
            } else {
                toast?.error(firstError(response) ?? t("ACCOUNT_SWITCHER_REMOVE_FAILED", accountName));
            }
        } catch (error: any) {
            toast?.error(error?.message ?? t("ACCOUNT_SWITCHER_REMOVE_FAILED", accountName));
        } finally {
            setIsLoading(false);
        }
    }

    async function softLogout() {
        if (!await showConfirm(t("SOFT_LOGOUT_CONFIRM"))) {
            return;
        }

        try {
            setIsLoading(true);
            const response = await sfetch("/api/User/SoftLogout", {
                method: "POST"
            });

            if (response.ok) {
                toast?.info(t("ACCOUNT_SWITCHER_SOFT_LOGOUT_DONE"));
                await loadCurrentUser();
                await loadOtherAccounts();
            } else {
                toast?.error(firstError(response) ?? t("ACCOUNT_SWITCHER_SOFT_LOGOUT_FAILED"));
            }
        } catch (error: any) {
            toast?.error(error?.message ?? t("ACCOUNT_SWITCHER_SOFT_LOGOUT_FAILED"));
        } finally {
            setIsLoading(false);
        }
    }

    return (
        <div class='account-switcher-container'>
            <div class="page-container">
                <div class="text-center section">
                    <Show when={currentUser() != null}>
                        <button 
                            onClick={() => navigate(`/me`)}
                            class="btn btn-secondary"
                            title="Go to your account"  
                        >
                            <i class="fa-solid fa-arrow-left"></i>
                            <i class="fa-solid fa-user"></i>
                        </button>
                    </Show>
                    <h1><Text val="ACCOUNT_SWITCHER"></Text></h1>
                </div>
                
                <div class='section'>
                    <h2><Text val="CURRENT_ACCOUNT"></Text></h2>
                    <Show when={currentUser()}>
                        <div class='current-account-card'>
                            <div class='account-header'>
                                <div class='account-info'>
                                    <h3 class='account-username minecraft-text'>{currentUser()?.username}</h3>
                                    <img 
                                        src={`https://mineskin.eu/helm/${currentUser()?.minecraftUUID}/100.png`} 
                                        class="account-avatar minecraft-avatar"
                                        alt={`${currentUser()?.username} avatar`}
                                    />
                                </div>
                                <button 
                                    onClick={softLogout} 
                                    disabled={isLoading()}
                                    class="btn btn-warning"
                                >
                                    <Text val="SOFT_LOGOUT"></Text>
                                </button>
                            </div>
                            <p class="text-secondary">
                                <Text val="SOFT_LOGOUT_DESCRIPTION"></Text>
                            </p>
                        </div>
                    </Show>
                    <Show when={!currentUser() && !isLoading()}>
                        <div class="not-logged-in-state">
                            <i class="fa-solid fa-user-slash not-logged-in-icon"></i>
                            <h3><Text val="NOT_LOGGED_IN"></Text></h3>
                            <p class="text-secondary"><Text val="PLEASE_LOGIN_FIRST"></Text></p>
                            <button 
                                onClick={() => {
                                    const urlParams = new URLSearchParams(window.location.search);
                                    const redirectUri = urlParams.get("redirectUri");
                                    const loginUrl = redirectUri ? `/login?redirectUri=${encodeURIComponent(redirectUri)}` : '/login';
                                    navigate(loginUrl);
                                }}
                                class="btn btn-primary btn-lg"
                            >
                                <i class="fa-solid fa-sign-in-alt"></i>
                                <Text val="LOGIN"></Text>
                            </button>
                        </div>
                    </Show>
                </div>

                <div class='section'>
                    <div class="flex justify-between items-center">
                        <h2><Text val="OTHER_ACCOUNTS"></Text></h2>
                        <button 
                            onClick={loadOtherAccounts} 
                            disabled={isLoading()}
                            class="btn btn-secondary btn-sm"
                            title="Refresh accounts"
                        >
                            <i class="fa-solid fa-refresh"></i>
                            <Text val="REFRESH"></Text>
                        </button>
                    </div>

                    <Show when={otherAccounts().length === 0 && !isLoading()}>
                        <div class="empty-state">
                            <i class="fa-solid fa-users empty-state-icon"></i>
                            <h3><Text val="NO_OTHER_ACCOUNTS"></Text></h3>
                            <p><Text val="USE_SOFT_LOGOUT_TO_ADD"></Text></p>
                        </div>
                    </Show>

                    <Show when={otherAccounts().length > 0}>
                        <div class='table-container'>
                            <table class='table'>
                                <thead>
                                    <tr>
                                        <th><Text val="USERNAME"></Text></th>
                                        <th><Text val="ACTIONS"></Text></th>
                                    </tr>
                                </thead>
                                <tbody>
                                    <For each={otherAccounts()}>
                                        {(account) => (
                                            <tr>
                                                <td class="font-weight-semibold minecraft-text">{account.username}</td>
                                                <td>
                                                    <div class="flex gap-md justify-end">
                                                        <button 
                                                            onClick={() => switchToAccount(account)}
                                                            disabled={isLoading()}
                                                            class="btn btn-primary btn-sm"
                                                            title={`Switch to ${account.username}`}
                                                        >
                                                            <i class="fa-solid fa-exchange-alt"></i>
                                                            <Text val="SWITCH"></Text>
                                                        </button>
                                                        <button 
                                                            onClick={() => deleteAccount(account.username)}
                                                            disabled={isLoading()}
                                                            class="danger"
                                                            title={`Remove ${account.username} from switcher`}
                                                        >
                                                            <i class="fa-solid fa-trash"></i>
                                                        </button>
                                                    </div>
                                                </td>
                                            </tr>
                                        )}
                                    </For>
                                </tbody>
                            </table>
                        </div>
                    </Show>

                    <Show when={isLoading()}>
                        <div class="text-center" style="padding: var(--spacing-4xl);">
                            <div class="spinner"></div>
                            <p class="text-secondary"><Text val="LOADING"></Text></p>
                        </div>
                    </Show>
                </div>

                <div class='section'>
                    <div class="card">
                        <div class="card-header">
                            <h3 class="card-title"><Text val="HOW_TO_USE"></Text></h3>
                        </div>
                        <div class="card-content">
                            <ul class="text-secondary">
                                <li><Text val="SOFT_LOGOUT_EXPLANATION"></Text></li>
                                <li><Text val="SWITCH_ACCOUNT_EXPLANATION"></Text></li>
                                <li><Text val="DELETE_ACCOUNT_EXPLANATION"></Text></li>
                            </ul>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
}
